import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

const DESKTOP_MEDIA_QUERY = "(min-width: 1025px)";

class ExpandableCard extends HTMLElement {
  constructor() {
    super();
    this.openDrawer = this.openDrawer.bind(this);
    this.openHoverState = this.openHoverState.bind(this);
    this.closeHoverState = this.closeHoverState.bind(this);
    this.closeDrawerState = this.closeDrawerState.bind(this);
    this.toggleClickState = this.toggleClickState.bind(this);
    this.onInteractionModeChange = this.onInteractionModeChange.bind(this);
    this.handleCardKeydown = this.handleCardKeydown.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector(".expandable-card__toggle");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
    if (!this.hasAttribute("role")) this.setAttribute("role", "button");
    this.setExpanded(false);
    this.addEventListener("keydown", this.handleCardKeydown);

    if (!this.trigger) return;
    this.trigger.setAttribute("tabindex", "-1");
    this.trigger.setAttribute("aria-hidden", "true");

    if (this.dataset.behavior === "drawer") {
      this.trigger.addEventListener("click", this.openDrawer);
      document.addEventListener("modal:closed", this.closeDrawerState);
      return;
    }

    this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.desktopMediaQuery.addEventListener("change", this.onInteractionModeChange);
    this.onInteractionModeChange();
  }

  disconnectedCallback() {
    this.removeEventListener("keydown", this.handleCardKeydown);
    if (this.trigger) {
      this.trigger.removeEventListener("click", this.openDrawer);
      this.trigger.removeEventListener("click", this.toggleClickState);
    }
    document.removeEventListener("modal:closed", this.closeDrawerState);
    this.desktopMediaQuery?.removeEventListener("change", this.onInteractionModeChange);
    this.disableHoverInteraction();
  }

  setExpanded(isExpanded) {
    const expanded = isExpanded ? "true" : "false";
    this.setAttribute("aria-expanded", expanded);
    this.trigger?.setAttribute("aria-expanded", expanded);
  }

  handleCardKeydown(event) {
    if (event.target !== this) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();

    if (this.dataset.behavior === "drawer") {
      this.openDrawer(event);
      return;
    }

    this.toggleClickState(event);
  }

  onInteractionModeChange() {
    if (this.desktopMediaQuery.matches) {
      this.enableHoverInteraction();
      this.disableClickInteraction();
    } else {
      this.disableHoverInteraction();
      this.enableClickInteraction();
    }
  }

  enableHoverInteraction() {
    if (this.hoverEnabled) return;
    this.hoverEnabled = true;
    this.addEventListener("pointerenter", this.openHoverState);
    this.addEventListener("pointerleave", this.closeHoverState);
    this.addEventListener("focusin", this.openHoverState);
    this.addEventListener("focusout", this.closeHoverState);
  }

  disableHoverInteraction() {
    if (!this.hoverEnabled) return;
    this.hoverEnabled = false;
    this.removeEventListener("pointerenter", this.openHoverState);
    this.removeEventListener("pointerleave", this.closeHoverState);
    this.removeEventListener("focusin", this.openHoverState);
    this.removeEventListener("focusout", this.closeHoverState);
    this.classList.remove("detail-open");
    this.setExpanded(false);
  }

  enableClickInteraction() {
    if (this.clickEnabled) return;
    this.clickEnabled = true;
    this.trigger.addEventListener("click", this.toggleClickState);
  }

  disableClickInteraction() {
    if (!this.clickEnabled) return;
    this.clickEnabled = false;
    this.trigger.removeEventListener("click", this.toggleClickState);
    this.classList.remove("detail-open");
    this.setExpanded(false);
  }

  openHoverState() {
    this.classList.add("detail-open");
    this.setExpanded(true);
  }

  closeHoverState(event) {
    if (event.type === "focusout" && this.contains(event.relatedTarget)) return;
    this.classList.remove("detail-open");
    this.setExpanded(false);
  }

  toggleClickState(event) {
    event.preventDefault();
    event.stopPropagation();
    const isOpen = this.classList.toggle("detail-open");
    this.setExpanded(isOpen);
  }

  closeDrawerState(event) {
    if (event.detail?.modal !== this.drawer) return;

    this.classList.remove("detail-open");
    this.setExpanded(false);
    this.drawer = null;
  }

  openDrawer(event) {
    event.preventDefault();
    const template = document.getElementById(this.dataset.drawerTemplate);
    if (!template) return;

    const drawer = template.content.firstElementChild.cloneNode(true);
    this.drawer = drawer;
    this.classList.add("detail-open");
    this.setExpanded(true);
    NextSkyTheme.getBody().appendChild(drawer);
    drawer.offsetHeight;

    requestAnimationFrame(() => {
      eventModal(drawer, "open", true, null, true);
      NextSkyTheme.global.rootToFocus = this;
    });
  }
}

if (!customElements.get("expandable-card")) {
  customElements.define("expandable-card", ExpandableCard);
}
